import { useTheme } from '@approvalmax/theme';
import { AmLogo } from '@approvalmax/ui';
import { Spacing } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { createAction } from 'modules/react-redux';
import { FC, memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { routingService } from 'services/routing';
import { getPath, Path } from 'urlBuilder';

import { SIGN_IN_WITH, SIGN_IN_WITH_FAILURE } from '../../actions';
import { LoginModuleData, PageActiveModule, SignupModuleData } from '../../reducers/page/moduleReducer';
import { getLoading, getModule } from '../../selectors/pageSelectors';
import LoginModule from './components/LoginModule/LoginModule';
import SignupModule from './components/SignupModule/SignupModule';
import { useQbooksVerifyEmailPopup } from './SignupLoginModule.hooks';
import { messages } from './SignupLoginModule.messages';
import {
    Card,
    Container,
    Content,
    MobileTitle,
    SsoDelimiter,
    StyledSsoPanel,
    SwitchButton,
    Switcher,
} from './SignupLoginModule.styles';
import { SignupLoginModuleProps } from './SignupLoginModule.types';

const SignupLoginModule: FC<SignupLoginModuleProps> = memo((props) => {
    const { activeModule, hideModuleSwitcher, onBlink } = props;

    const dispatch = useDispatch();
    const module = useSelector(getModule<LoginModuleData | SignupModuleData>);
    const [email, setEmail] = useState(module.email ?? '');

    const isLoading = useSelector(getLoading);

    const isLogin = activeModule === PageActiveModule.LoginModule;

    const showSignup = () => {
        routingService.replace(
            getPath(Path.register, {
                email,
                app: module.app,
            })
        );
    };

    const showSignin = () => {
        routingService.replace(
            getPath(Path.login, {
                workspace: module.app,
                email,
            })
        );

        if (!isLogin) {
            amplitudeService.sendData('signup: click on log in button');
        }
    };

    const onSsoSignInSubmit = useCallback(
        (options: { oauthProvider: domain.OAuthProvider; startingPoint: domain.SsoStartingPoint }) => {
            dispatch(
                createAction(SIGN_IN_WITH, {
                    oauthProvider: options.oauthProvider,
                    startingPoint: options.startingPoint,
                })
            );
        },
        [dispatch]
    );

    const onSsoSignInError = useCallback(() => {
        dispatch(createAction(SIGN_IN_WITH_FAILURE));
    }, [dispatch]);

    useQbooksVerifyEmailPopup();

    const { theme } = useTheme();
    const isMobileScreen = useMedia(`(max-width: ${theme.container.breakpoint.xsmall})`);

    return (
        <Container>
            <Card>
                {isMobileScreen && (
                    <>
                        <MobileTitle>{isLogin ? messages.loginText : messages.signUpText}</MobileTitle>

                        <Spacing height={8} />

                        <AmLogo width={207} height={30} />
                    </>
                )}

                {hideModuleSwitcher ? (
                    isMobileScreen && <Spacing height={40} />
                ) : (
                    <>
                        {isMobileScreen && <Spacing height={20} />}

                        <Switcher>
                            <SwitchButton type='button' $active={!isLogin} onClick={showSignup}>
                                {messages.signUpText}
                            </SwitchButton>

                            <SwitchButton type='button' $active={isLogin} onClick={showSignin}>
                                {messages.loginText}
                            </SwitchButton>
                        </Switcher>

                        <Spacing height={isMobileScreen ? 20 : 28} />
                    </>
                )}

                <Content>
                    {isLogin ? (
                        <LoginModule onBlink={onBlink!} setEmail={setEmail} />
                    ) : (
                        <SignupModule setEmail={setEmail} />
                    )}
                </Content>

                <SsoDelimiter>{isLogin ? messages.loginWith : messages.signUpWith}</SsoDelimiter>

                <StyledSsoPanel
                    variant={isLogin ? 'signin' : 'signup'}
                    app={module.app}
                    disabled={isLoading}
                    afterLoginRedirectUrl={module.type === PageActiveModule.LoginModule ? module.postAction : undefined}
                    width={isMobileScreen ? 'auto' : undefined}
                    onSubmit={onSsoSignInSubmit}
                    onError={onSsoSignInError}
                />
            </Card>
        </Container>
    );
});

export default SignupLoginModule;
